/**
 * App Check for our own /api calls (C-16): the public signup (`signupApi`) and
 * the assistant (`aiApi`) accept only requests that carry a token proving they
 * come from this web app (`functions/src/lib/app-check.ts`).
 *
 * The attestation is reCAPTCHA Enterprise (score key, no challenge shown). It
 * starts lazily, on the first call that needs a token, so pages that never
 * touch signup or the assistant don't load reCAPTCHA, and auto-refresh is off:
 * a token is minted only when one is needed and has expired. Each mint is one
 * reCAPTCHA assessment, and the free tier is 10,000 a month.
 *
 * E2E and the smoke test run as a registered debug token instead: the harness
 * sets `self.FIREBASE_APPCHECK_DEBUG_TOKEN` before the page loads, and the SDK
 * then exchanges that instead of running reCAPTCHA
 * (docs/runbooks/2026-10-28-app-check-rollout.md).
 */
import type { FirebaseApp } from "firebase/app";
import {
  type AppCheck,
  getToken,
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
} from "firebase/app-check";

/** Header the functions read the token from. */
export const APP_CHECK_HEADER = "X-Firebase-AppCheck";

/** Error code of the functions' 401 when they refuse a request for its token. */
export const APP_CHECK_FAILED = "app_check_failed";

/**
 * Returns a function that mints an App Check token for the app `app()`, or
 * `null` when App Check is off (no site key: local dev, emulators) or the token
 * can't be minted (reCAPTCHA blocked, network down). Never throws: whether a
 * request without a token goes through is the server's call.
 */
export function createAppCheckTokenSource(opts: {
  readonly app: () => FirebaseApp;
  readonly siteKey: () => string | undefined;
}): () => Promise<string | null> {
  let appCheck: AppCheck | null = null;
  return async () => {
    const siteKey = opts.siteKey();
    if (!siteKey) return null;
    try {
      appCheck ??= initializeAppCheck(opts.app(), {
        provider: new ReCaptchaEnterpriseProvider(siteKey),
        isTokenAutoRefreshEnabled: false,
      });
      const { token } = await getToken(appCheck);
      return token;
    } catch (e) {
      console.warn("App Check token unavailable", e);
      return null;
    }
  };
}

/** The header to merge into a fetch, or none without a token. */
export function appCheckHeader(token: string | null): Record<string, string> {
  return token ? { [APP_CHECK_HEADER]: token } : {};
}

/** Thrown when the functions refused a request for its App Check token. */
export class AppCheckRefusedError extends Error {
  constructor() {
    super(APP_CHECK_FAILED);
    this.name = "AppCheckRefusedError";
  }
}

/** True when `res` is the functions' App Check refusal (401 `app_check_failed`). */
export async function isAppCheckRefusal(res: Response): Promise<boolean> {
  if (res.status !== 401) return false;
  const body = (await res
    .clone()
    .json()
    .catch(() => null)) as { error?: unknown } | null;
  return body?.error === APP_CHECK_FAILED;
}
